import EventAgent from '@/common/utils/event-agent-wrapper';
import { useCallback, useMemo, useEffect } from 'react';
import { useUser } from '@/common/contexts/user-context';
import { HotmartRum } from '@hot-observability-js/react';

import { SYSTEM, EVENT_CONFIG, EventKey, ONBOARDING_SESSION_KEY } from '@/common/constants';

import { detectMobile, getPlatformType, getEventScreenUrl } from '@/common/utils/platform';

import { SendEventParams } from '@/types';
import { useEventDeduplication } from './useEventDeduplication';
import { ucodeManager } from '@/common/utils/ucode-manager';

const isProd = process.env.TARGET_ENV === 'production' || process.env.TARGET_ENV === 'experimental';
EventAgent.mode = isProd ? 'production' : 'staging';

interface UseEventAgentResponse {
  sendCustomEvent: (params: SendEventParams) => Promise<void>;
  trackEvent: (eventKey: EventKey, extraParams?: Record<string, unknown>) => Promise<void>;
  trackEventWithDedup: (
    eventKey: EventKey,
    extraParams?: Record<string, unknown>,
    windowMs?: number
  ) => Promise<void>;
}

export const useEventAgent = (): UseEventAgentResponse => {
  const { id: userId, ucode, isFromBrazil, entityType } = useUser();
  const userContextData = useMemo(
    () => ({ ucode, userId, isFromBrazil, entityType }),
    [ucode, userId, isFromBrazil, entityType]
  );
  const isMobile = useMemo(() => detectMobile(), []);
  const platformType = useMemo(() => getPlatformType(), []);
  const eventScreen = useMemo(() => getEventScreenUrl(), []);

  const sendCustomEvent = useCallback(
    async ({
      entity = 'onboarding',
      action = 'interact',
      event = {},
      system = SYSTEM,
    }: SendEventParams) => {
      try {
        await EventAgent.send({
          system,
          entity,
          action,
          event: {
            device: isMobile ? 'mobile' : 'desktop',
            platform: platformType,
            eventScreen,
            ...event,
          },
        });
      } catch (error) {
        HotmartRum.captureException(error, {
          extra: {
            event: {
              entity,
              action,
              event,
            },
          },
        });
      }
    },
    [isMobile, platformType, eventScreen]
  );

  const trackEvent = useCallback(
    async (eventKey: EventKey, extraParams = {}): Promise<void> => {
      try {
        const config = EVENT_CONFIG[eventKey];

        if (!config) {
          HotmartRum.captureMessage(`Event configuration not found for key: ${eventKey}`, {
            extra: {
              eventKey,
            },
          });
          return;
        }

        const executeTracking = async () => {
          const storageUcode = ucodeManager.getUcode();
          const finalUcode = storageUcode || userContextData?.ucode || '';
          const sessionKey = sessionStorage.getItem(ONBOARDING_SESSION_KEY);

          await sendCustomEvent({
            event: {
              eventName: config.eventName,
              eventType: config.eventType,
              eventScreen: config.eventScreen,
              extraParams: JSON.stringify({
                pageUrl: eventScreen,
                ...extraParams,
                ...(sessionKey && { sessionKey }),
              }),
              ucode: finalUcode,
              userId: userContextData?.userId || '',
              isFromBrazil: userContextData?.isFromBrazil || false,
              entityType: userContextData?.entityType || '',
            },
          });
        };

        const currentUcode = ucodeManager.getUcode();
        const contextUcode = userContextData?.ucode;

        if (!currentUcode && !contextUcode) {
          ucodeManager.queueEventIfNoUcode(executeTracking);
        } else {
          await executeTracking();
        }
      } catch (error) {
        HotmartRum.captureException(error, {
          extra: {
            eventKey,
          },
        });
      }
    },
    [sendCustomEvent, userContextData]
  );

  const { trackEventWithDedup, cleanup } = useEventDeduplication(trackEvent);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    sendCustomEvent,
    trackEvent,
    trackEventWithDedup,
  };
};
